import React from 'react';
import { AppShell, Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { MineMinerNavbar } from '../components/layout/MineMinerNavbar';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Footer } from '../components/layout/Footer';
import { RouteErrorBoundary, ErrorFallback } from '../components/ErrorBoundary';
import { SessionTimeoutWarning } from '../components/auth/SessionTimeoutWarning/SessionTimeoutWarning';
import { useSessionMonitor } from '../hooks/useSessionMonitor';

/**
 * MineMinerLayout Component
 *
 * Wraps every /MineMiner/* route with the same chrome shape as AdminLayout
 * (header + collapsible sidebar + breadcrumbs + footer + session timeout),
 * but with a MineMiner-scoped sidebar — see MineMinerNavbar.
 *
 * Silo rule: this layout intentionally does NOT cross-link to MentionedMe
 * admin/customer pages from the sidebar. The shared Header user menu is
 * the only cross-app surface (Profile / Settings / Logout).
 *
 * Authentication + role gating happens in MineMinerRoute, which wraps
 * this layout externally in AppRoutes.
 */
export const MineMinerLayout: React.FC = (): React.ReactElement => {
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure();

  // sprint-130 S4 — proactively slide the rep session while the app is in use.
  useSessionMonitor();

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileNavOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Header onMenuClick={toggleMobileNav} />
      </AppShell.Header>

      <AppShell.Navbar onClick={closeMobileNav}>
        <MineMinerNavbar />
      </AppShell.Navbar>

      <AppShell.Main>
        <Stack justify="space-between" mih="calc(100vh - 60px)">
          <Box>
            <Box mb="md">
              <Breadcrumbs />
            </Box>

            <RouteErrorBoundary fallback={ErrorFallback}>
              <Outlet />
            </RouteErrorBoundary>
          </Box>

          <Footer />
        </Stack>
      </AppShell.Main>

      <SessionTimeoutWarning />
    </AppShell>
  );
};
